import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import validator from "validator";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { HiPlus, HiX, HiSearch, HiOfficeBuilding, HiPencil, HiCheck } from "react-icons/hi";
import { FaCloudUploadAlt } from "react-icons/fa";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { BsPersonFill } from "react-icons/bs";
import MyWizard from "../Vendor/MyWizard";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { useUpdateVendorMutation } from "../../../features/admin/vendor/vendorApi";

const modalStyle = {
  position: "absolute", top: "50%", left: "50%",
  transform: "translate(-50%, -50%)",
  width: "92%", maxWidth: "1100px", height: "90%",
  bgcolor: "background.paper", boxShadow: 24, borderRadius: 3,
  overflow: "hidden", display: "flex", flexDirection: "column",
};

const ADDR_INIT = { homeAddress: "", state: "", country: "", district: "", landMark: "", city: "", pincode: "" };
const SOCIAL_INIT = { facebook: "", youtube: "", instagram: "", twitter: "" };

const FIELD_LABELS = {
  name: "Name", email: "Email", mobile: "Mobile", gstin: "GSTIN",
  website: "Company URL", contact_person_name: "Contact Person", contact_person_number: "Contact Number",
  qr_type: "QR Type", status: "Status", panel_status: "Panel Status",
  industry_id: "Industry", industry_name: "Industry Name", address: "Address",
  socials: "Social Media", logo: "Logo", password: "Password",
};

// ── Password rules (same as onboarding wizard) ────────────────────────────────
const PW_RULES = [
  { label: "At least 8 characters",  test: p => p.length >= 8 },
  { label: "One uppercase letter",    test: p => /[A-Z]/.test(p) },
  { label: "One lowercase letter",    test: p => /[a-z]/.test(p) },
  { label: "One number",              test: p => /[0-9]/.test(p) },
  { label: "One special character",   test: p => /[^A-Za-z0-9]/.test(p) },
];

const PwStrengthBar = ({ password }) => {
  if (!password) return null;
  const score = PW_RULES.filter(r => r.test(password)).length;
  const seg = [
    score >= 1 ? (score <= 2 ? "#f87171" : score <= 3 ? "#fbbf24" : "#10b981") : "#e5e7eb",
    score >= 3 ? (score <= 3 ? "#fbbf24" : "#10b981") : "#e5e7eb",
    score >= 5 ? "#10b981" : "#e5e7eb",
  ];
  const label = score <= 2 ? "Weak" : score <= 3 ? "Fair" : score === 4 ? "Good" : "Strong";
  const lc = score <= 2 ? "#ef4444" : score <= 3 ? "#f59e0b" : "#059669";
  return (
    <div style={{ marginTop: "8px" }}>
      <div style={{ display: "flex", gap: "4px" }}>
        {seg.map((c, i) => <div key={i} style={{ height: "6px", flex: 1, borderRadius: "999px", background: c, transition: "background 0.2s" }} />)}
      </div>
      <p style={{ fontSize: "11px", marginTop: "4px", fontWeight: 600, color: lc }}>{label}</p>
    </div>
  );
};

const PwRules = ({ password }) => {
  if (!password) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: "8px" }}>
      {PW_RULES.map(r => {
        const ok = r.test(password);
        return (
          <div key={r.label} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: ok ? "#059669" : "#9ca3af" }}>
            <span style={{ display: "flex", width: "13px", height: "13px", alignItems: "center", justifyContent: "center", borderRadius: "999px", border: ok ? "none" : "1px solid #d1d5db", background: ok ? "#059669" : "transparent", color: "#fff", fontSize: "8px", fontWeight: 700, flexShrink: 0 }}>
              {ok ? "✓" : ""}
            </span>
            {r.label}
          </div>
        );
      })}
    </div>
  );
};

const displayVal = (key, val) => {
  if (key === "logo") return Array.isArray(val) ? val.filter(Boolean).join(", ") || "—" : "—";
  if (key === "address" || key === "socials") {
    try { const o = typeof val === "string" ? JSON.parse(val) : val; return Object.entries(o).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" | ") || "—"; }
    catch { return String(val); }
  }
  if (key === "password") return val ? "••••••••" : "—";
  return String(val ?? "—");
};

// ── Inline update modal ──────────────────────────────────────────────────────
const UpdateClientModal = ({ client, onClose, onSuccess }) => {
  const [name, setName] = useState(client.name || "");
  const [email, setEmail] = useState(client.email || "");
  const [mobile, setMobile] = useState(client.mobile || "");
  const [gstin, setGstin] = useState(client.gstin || "");
  const [website, setWebsite] = useState(client.website || "");
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [contactPerson, setContactPerson] = useState(client.contact_person_name || "");
  const [contactPersonNum, setContactPersonNum] = useState(client.contact_person_number || "");
  const [address, setAddress] = useState({ ...ADDR_INIT, ...(client.address || {}) });
  const [socials, setSocials] = useState({ ...SOCIAL_INIT, ...(client.socials || {}) });
  const [industryId, setIndustryId] = useState(client.industry_id || 0);
  const [qrType, setQrType] = useState(String(client.qr_type || "0"));
  const [status, setStatus] = useState(client.status || 1);
  const [panelStatus, setPanelStatus] = useState(client.panel_status || 0);
  const [qrPrefix, setQrPrefix] = useState(client.qr_prefix || "");
  const [thumbLogo, setThumbLogo] = useState((client.logo || [])[0] || "");
  const [navLogo, setNavLogo] = useState((client.logo || [])[1] || "");
  const [thumbFile, setThumbFile] = useState(null);
  const [navFile, setNavFile] = useState(null);
  const [logoType, setLogoType] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [showRePwd, setShowRePwd] = useState(false);
  const [industryData, setIndustryData] = useState([]);
  const [step, setStep] = useState("form"); // "form" | "review"
  const [diff, setDiff] = useState([]);     // [{ key, label, before, after }]
  const [payload, setPayload] = useState(null);

  const [emailErr, setEmailErr] = useState(null);
  const [mobileErr, setMobileErr] = useState(null);

  const [updateVendor, { data: updateData, isLoading: updating, error: updateError }] = useUpdateVendorMutation();
  const [uploadImage, { data: uploadData, isLoading: uploading, error: uploadError }] = useUploadSingleFileMutation();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/admin/industry`, {
      headers: { Authorization: "Bearer " + sessionStorage.getItem("access_token") },
    }).then(r => setIndustryData(r.data.body || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (uploadData) {
      if (logoType === 0) setThumbLogo(uploadData.body.fileLink);
      else setNavLogo(uploadData.body.fileLink);
    }
    if (uploadError) toast.error("Image upload failed");
  }, [uploadData, uploadError]);

  useEffect(() => {
    if (updateData) { toast.success(updateData.message || "Updated"); onSuccess(); onClose(); }
    if (updateError) toast.error("Update failed");
  }, [updateData, updateError]);

  const uploadImg = (file, type) => {
    const fd = new FormData();
    fd.append("image", file[0]);
    setLogoType(type);
    uploadImage({ body: fd, token: sessionStorage.getItem("access_token") });
  };

  const buildDiff = () => {
    const changes = [];
    const orig = client;

    const check = (key, label, origVal, newVal, serialise = v => v) => {
      const a = serialise(origVal ?? ""), b = serialise(newVal ?? "");
      if (String(a) !== String(b)) changes.push({ key, label, before: origVal, after: newVal });
    };

    check("name", "Name", orig.name, name);
    check("email", "Email", orig.email, email);
    check("mobile", "Mobile", orig.mobile, mobile);
    check("gstin", "GSTIN", orig.gstin, gstin);
    check("website", "Company URL", orig.website || "", website);
    check("contact_person_name", "Contact Person", orig.contact_person_name || "", contactPerson);
    check("contact_person_number", "Contact Number", orig.contact_person_number || "", contactPersonNum);
    check("qr_type", "QR Type", String(orig.qr_type || "0"), qrType);
    check("status", "Status", String(orig.status || 1), String(status));
    check("panel_status", "Panel Status", String(orig.panel_status || 0), String(panelStatus));
    check("industry_id", "Industry", String(orig.industry_id || ""), String(industryId));
    check("qr_prefix", "QR Prefix", String(orig.qr_prefix || ""), String(qrPrefix));
    if (password) changes.push({ key: "password", label: "Password", before: "(existing)", after: "••••••••" });

    // Address
    const origAddr = JSON.stringify({ ...ADDR_INIT, ...(orig.address || {}) });
    if (JSON.stringify(address) !== origAddr)
      changes.push({ key: "address", label: "Address", before: orig.address, after: address });

    // Socials
    const origSocials = JSON.stringify({ ...SOCIAL_INIT, ...(orig.socials || {}) });
    if (JSON.stringify(socials) !== origSocials)
      changes.push({ key: "socials", label: "Social Media", before: orig.socials, after: socials });

    // Logo
    const origThumb = (orig.logo || [])[0] || "";
    const origNav = (orig.logo || [])[1] || "";
    if (thumbLogo !== origThumb || navLogo !== origNav)
      changes.push({ key: "logo", label: "Logo", before: [origThumb, origNav], after: [thumbLogo, navLogo] });

    return changes;
  };

  const buildPayload = (changedKeys) => {
    const p = {};
    changedKeys.forEach(({ key }) => {
      if (key === "name") p.name = name;
      else if (key === "email") p.email = email;
      else if (key === "mobile") p.mobile = mobile;
      else if (key === "gstin") p.gstin = gstin;
      else if (key === "website") p.website = website;
      else if (key === "contact_person_name") p.contact_person_name = contactPerson;
      else if (key === "contact_person_number") p.contact_person_number = contactPersonNum;
      else if (key === "qr_type") p.qr_type = qrType;
      else if (key === "qr_prefix") p.qr_prefix = qrPrefix;
      else if (key === "status") p.status = String(status);
      else if (key === "panel_status") p.panel_status = String(panelStatus);
      else if (key === "industry_id") {
        let industryName = client.industry_name || "";
        let user_types = client.user_types || [];
        industryData.forEach(d => { if (d.industry_id == industryId) { industryName = d.name; user_types = d.users; } });
        p.industry_id = industryId;
        p.industry_name = industryName;
        p.user_types = JSON.stringify(user_types);
      }
      else if (key === "password") p.password = password;
      else if (key === "address") p.address = JSON.stringify(address);
      else if (key === "socials") p.socials = JSON.stringify(socials);
      else if (key === "logo") p.logo = [thumbLogo, navLogo];
    });
    return p;
  };

  const handleReview = (e) => {
    e.preventDefault();
    if (password && PW_RULES.some(r => !r.test(password))) { toast.error("Password doesn't meet all requirements"); return; }
    if (password && rePassword !== password) { toast.error("Passwords do not match"); return; }
    if (emailErr || mobileErr || !name || !email || !gstin) {
      toast.error("Fix form errors before reviewing"); return;
    }
    const changes = buildDiff();
    if (changes.length === 0) { toast("No changes detected", { icon: "ℹ️" }); return; }
    const p = buildPayload(changes);
    setDiff(changes);
    setPayload(p);
    setStep("review");
  };

  const handleConfirm = () => {
    updateVendor({ id: client.company_id, token: sessionStorage.getItem("access_token"), body: payload });
  };

  const addr = (k) => (e) => setAddress(p => ({ ...p, [k]: e.target.value }));
  const soc = (k) => (e) => setSocials(p => ({ ...p, [k]: e.target.value }));

  const inp = { border: "1px solid #e2e8f0", borderRadius: "6px", padding: "8px 10px", width: "100%", fontSize: "13px", outline: "none", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
  const field = { marginBottom: "14px" };

  if (step === "review") {
    return (
      <div>
        <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
          Review <strong style={{ color: "#0f172a" }}>{diff.length}</strong> change{diff.length !== 1 ? "s" : ""} before saving. Only these fields will be sent to the server.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginBottom: "24px" }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
              {["Field", "Before", "After"].map(h => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#64748b", fontSize: "12px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diff.map(({ key, label, before, after }) => (
              <tr key={key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "10px 14px", fontWeight: 600, color: "#374151" }}>{label}</td>
                <td style={{ padding: "10px 14px", color: "#ef4444", background: "#fef2f2", maxWidth: "300px", wordBreak: "break-all" }}>
                  {displayVal(key, before)}
                </td>
                <td style={{ padding: "10px 14px", color: "#059669", background: "#f0fdf4", maxWidth: "300px", wordBreak: "break-all" }}>
                  {displayVal(key, after)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button type="button" onClick={() => setStep("form")} style={{ padding: "9px 20px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "7px", fontSize: "14px", cursor: "pointer" }}>
            ← Back to Edit
          </button>
          <button type="button" onClick={handleConfirm} disabled={updating}
            style={{ padding: "9px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "7px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
            {updating ? "Saving…" : `Confirm & Save ${diff.length} Change${diff.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleReview}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
        <div style={field}><label style={lbl}>Name *</label><input style={inp} value={name} onChange={e => setName(e.target.value)} required /></div>
        <div style={field}>
          <label style={lbl}>Email *</label>
          <input style={{ ...inp, borderColor: emailErr ? "#ef4444" : "#e2e8f0" }} value={email}
            onChange={e => { setEmail(e.target.value); setEmailErr(!validator.isEmail(e.target.value) && e.target.value ? "Invalid email" : null); }} required />
          {emailErr && <p style={{ color: "#ef4444", fontSize: "11px", margin: "3px 0 0" }}>{emailErr}</p>}
        </div>
        <div style={field}>
          <label style={lbl}>Mobile</label>
          <input style={{ ...inp, borderColor: mobileErr ? "#ef4444" : "#e2e8f0" }} value={mobile}
            onChange={e => { setMobile(e.target.value); setMobileErr(!validator.isMobilePhone(e.target.value, ["en-IN"]) && e.target.value ? "Invalid mobile" : null); }} />
          {mobileErr && <p style={{ color: "#ef4444", fontSize: "11px", margin: "3px 0 0" }}>{mobileErr}</p>}
        </div>
        <div style={field}>
          <label style={lbl}>Industry</label>
          <select style={inp} value={industryId} onChange={e => setIndustryId(e.target.value)}>
            <option value={0}>Select Industry</option>
            {industryData.map(d => <option key={d.industry_id} value={d.industry_id}>{d.name}</option>)}
          </select>
        </div>
        <div style={field}><label style={lbl}>Contact Person</label><input style={inp} value={contactPerson} onChange={e => setContactPerson(e.target.value)} /></div>
        <div style={field}><label style={lbl}>Contact Number</label><input style={inp} value={contactPersonNum} onChange={e => setContactPersonNum(e.target.value)} /></div>
        <div style={field}><label style={lbl}>Company URL</label><input style={inp} value={website} onChange={e => setWebsite(e.target.value)} /></div>
        <div style={field}><label style={lbl}>GSTIN *</label><input style={inp} value={gstin} maxLength={16} onChange={e => setGstin(e.target.value)} required /></div>
        <div style={field}>
          <label style={lbl}>QR Type</label>
          <select style={inp} value={qrType} onChange={e => setQrType(e.target.value)}>
            <option value="0">Standard QR</option><option value="1">Enterprise QR</option>
          </select>
        </div>
        <div style={field}>
          <label style={lbl}>Status</label>
          <select style={inp} value={status} onChange={e => setStatus(e.target.value)}>
            <option value={1}>Active</option><option value={2}>Inactive</option>
          </select>
        </div>
        <div style={field}>
          <label style={lbl}>QR Prefix</label>
          <input style={inp} value={qrPrefix} onChange={e => setQrPrefix(e.target.value)} />
        </div>
        <div style={field}>
          <label style={lbl}>Panel Status</label>
          <select style={inp} value={panelStatus} onChange={e => setPanelStatus(e.target.value)}>
            <option value={0}>Demo</option><option value={1}>Testing / Staging</option>
            <option value={2}>Pre-Production</option><option value={3}>Production (Live)</option>
          </select>
        </div>
      </div>

      {/* Password block */}
      <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "16px 20px", marginBottom: "20px", background: "#f8fafc" }}>
        <p style={{ fontSize: "12px", fontWeight: 700, color: "#374151", margin: "0 0 2px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Change Password</p>
        <p style={{ fontSize: "12px", color: "#94a3b8", margin: "0 0 14px" }}>Leave both fields blank to keep the existing password.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <div style={field}>
            <label style={lbl}>New Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...inp, paddingRight: "36px" }} type={showPwd ? "text" : "password"} value={password}
                autoComplete="new-password"
                onChange={e => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPwd(v => !v)}
                style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex" }}>
                {showPwd ? <FiEyeOff size={15} /> : <FiEye size={15} />}
              </button>
            </div>
            <PwStrengthBar password={password} />
            <PwRules password={password} />
          </div>
          <div style={field}>
            <label style={lbl}>Confirm Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...inp, paddingRight: "36px" }} type={showRePwd ? "text" : "password"} value={rePassword}
                autoComplete="new-password"
                onChange={e => setRePassword(e.target.value)} />
              <button type="button" onClick={() => setShowRePwd(v => !v)}
                style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex" }}>
                {showRePwd ? <FiEyeOff size={15} /> : <FiEye size={15} />}
              </button>
            </div>
            {rePassword.length > 0 && (
              <p style={{ marginTop: "8px", fontSize: "11px", display: "flex", alignItems: "center", gap: "4px", fontWeight: 600, color: rePassword === password ? "#059669" : "#ef4444" }}>
                {rePassword === password ? <HiCheck size={12} /> : <HiX size={12} />}
                {rePassword === password ? "Passwords match" : "Passwords do not match"}
              </p>
            )}
          </div>
        </div>
      </div>

      <p style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", margin: "4px 0 10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Address</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0 16px" }}>
        {[["Landmark", "landMark"], ["State", "state"], ["City", "city"], ["District", "district"], ["Country", "country"], ["Pincode", "pincode"]].map(([label, key]) => (
          <div key={key} style={field}><label style={lbl}>{label}</label><input style={inp} value={address[key]} onChange={addr(key)} /></div>
        ))}
        <div style={{ ...field, gridColumn: "1 / -1" }}><label style={lbl}>Head Office Address</label><textarea style={{ ...inp, height: "60px", resize: "none" }} value={address.homeAddress} onChange={addr("homeAddress")} /></div>
      </div>

      <p style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", margin: "4px 0 10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Social Media</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0 16px" }}>
        {["facebook", "instagram", "twitter", "youtube"].map(k => (
          <div key={k} style={field}><label style={lbl}>{k.charAt(0).toUpperCase() + k.slice(1)}</label><input style={inp} value={socials[k]} onChange={soc(k)} /></div>
        ))}
      </div>

      <p style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", margin: "4px 0 10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Logos</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
        {[
          { label: "Login Page Logo", logo: thumbLogo, file: thumbFile, setFile: setThumbFile, type: 0 },
          { label: "Navbar Logo", logo: navLogo, file: navFile, setFile: setNavFile, type: 1 },
        ].map(({ label, logo, file, setFile, type }) => (
          <div key={type} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px" }}>
            <p style={{ ...lbl, marginBottom: "8px" }}>{label}</p>
            <input id={`logo-${type}`} type="file" accept="image/*" style={{ display: "none" }} onChange={e => setFile(e.target.files)} />
            <label htmlFor={`logo-${type}`} style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "7px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
              <FaCloudUploadAlt /> Choose {file?.[0] && <span style={{ color: "#64748b" }}>{file[0].name.slice(0, 14)}</span>}
            </label>
            {file?.[0] && (
              <button type="button" onClick={() => uploadImg(file, type)} disabled={uploading && logoType === type}
                style={{ marginLeft: "8px", padding: "7px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", fontSize: "12px", cursor: "pointer" }}>
                {uploading && logoType === type ? "Uploading…" : "Upload"}
              </button>
            )}
            {logo && <img src={logo} alt="" style={{ display: "block", marginTop: "8px", maxHeight: "48px", borderRadius: "4px" }} />}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button type="button" onClick={onClose} style={{ padding: "9px 20px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "7px", fontSize: "14px", cursor: "pointer" }}>Cancel</button>
        <button type="submit" style={{ padding: "9px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "7px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
          Review Changes →
        </button>
      </div>
    </form>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const MasterHome = () => {
  const navigate = useNavigate();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [refetch, setRefetch] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const fetchClients = (q = "") => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_BASE_URL}/admin/company`, {
      params: q ? { search: q } : {},
      headers: { Authorization: "Bearer " + sessionStorage.getItem("access_token") },
    })
      .then(r => { if (r.data.success) setAllClients(r.data.body); })
      .catch(err => toast.error(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, [refetch]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchClients(val), 400);
  };

  const getInitials = (name = "") => name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const getUserTypesCount = (ut) => Array.isArray(ut) ? ut.length : 0;

  const COLS = ["Client", "Tenant ID", "Contact", "GSTIN", "Theme", "Industry", "User Types", "Registered", ""];

  return (
    <>
      {/* Register modal */}
      <Modal keepMounted open={registerOpen} onClose={() => setRegisterOpen(false)}>
        <Box sx={modalStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
            <div>
              <h5 style={{ margin: 0, fontSize: "17px", fontWeight: 600, color: "#0f172a" }}>Register New Client</h5>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b", marginTop: "2px" }}>Fill in the details to onboard a new client.</p>
            </div>
            <button onClick={() => setRegisterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", color: "#64748b" }}>
              <HiX size={20} />
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px 24px" }}>
            <MyWizard handleClose={() => setRegisterOpen(false)} refetch={setRefetch} />
          </div>
        </Box>
      </Modal>

      {/* Update modal */}
      <Modal open={!!editClient} onClose={() => setEditClient(null)}>
        <Box sx={modalStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
            <div>
              <h5 style={{ margin: 0, fontSize: "17px", fontWeight: 600, color: "#0f172a" }}>Update Client</h5>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b", marginTop: "2px" }}>{editClient?.name}</p>
            </div>
            <button onClick={() => setEditClient(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", color: "#64748b" }}>
              <HiX size={20} />
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px 24px" }}>
            {editClient && (
              <UpdateClientModal
                key={editClient.company_id}
                client={editClient}
                onClose={() => setEditClient(null)}
                onSuccess={() => setRefetch(p => !p)}
              />
            )}
          </div>
        </Box>
      </Modal>

      <div className="page-content">
        <div style={{ padding: "24px" }}>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h4 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#0f172a" }}>Clients</h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b", marginTop: "3px" }}>
                {loading ? "Loading…" : `${allClients.length} client${allClients.length !== 1 ? "s" : ""} registered`}
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => navigate("/onboardings")} style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "9px 16px", background: "#fff", color: "#374151", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}>
                Onboardings
              </button>
              <button onClick={() => setRegisterOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "9px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
                <HiPlus size={16} /> Register Client
              </button>
            </div>
          </div>

          <div style={{ position: "relative", marginBottom: "20px", maxWidth: "400px" }}>
            <HiSearch size={16} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input type="text" placeholder="Search by name, email, mobile or GSTIN…" value={search} onChange={handleSearch}
              style={{ width: "100%", padding: "10px 12px 10px 36px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", color: "#0f172a", outline: "none", background: "#fff", boxSizing: "border-box" }} />
          </div>

          <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  {COLS.map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#64748b", whiteSpace: "nowrap", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>Loading clients…</td></tr>
                ) : allClients.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>
                      <HiOfficeBuilding size={32} style={{ marginBottom: "8px", opacity: 0.4 }} />
                      <div>{search ? "No clients match your search." : "No clients registered yet."}</div>
                    </td>
                  </tr>
                ) : allClients.map(c => {
                  const utCount = getUserTypesCount(c.user_types);
                  return (
                    <tr key={c.company_id} style={{ borderBottom: "1px solid #f1f5f9" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>

                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          {c.logo?.[0] ? (
                            <img src={c.logo[0]} alt={c.name}
                              style={{ width: "36px", height: "36px", borderRadius: "8px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                              onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                            />
                          ) : null}
                          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#e0e7ff", display: c.logo?.[0] ? "none" : "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <HiOfficeBuilding size={18} style={{ color: "#4f46e5" }} />
                          </div>
                          <span style={{ fontWeight: 600, color: "#0f172a" }}>{c.name}</span>
                        </div>
                      </td>

                      <td style={{ padding: "14px 16px", color: "#64748b", fontFamily: "monospace", fontSize: "12px" }}>#{c.tenant_id}</td>

                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ color: "#374151" }}>{c.email}</div>
                        <div style={{ color: "#94a3b8", fontSize: "12px" }}>{c.mobile}</div>
                      </td>

                      <td style={{ padding: "14px 16px", color: "#374151", fontFamily: "monospace", fontSize: "12px" }}>{c.gstin || "—"}</td>

                      <td style={{ padding: "14px 16px", color: "#374151", fontSize: "12px" }}>{c.theme_name || "—"}</td>

                      <td style={{ padding: "14px 16px", color: "#374151" }}>{c.industry_name || "—"}</td>

                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <BsPersonFill size={13} style={{ color: "#6366f1" }} />
                          <span style={{ fontWeight: 600, color: "#6366f1" }}>{utCount}</span>
                          <span style={{ color: "#94a3b8", fontSize: "12px" }}>type{utCount !== 1 ? "s" : ""}</span>
                        </div>
                        {utCount > 0 && (
                          <div style={{ marginTop: "4px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {(Array.isArray(c.user_types) ? c.user_types : []).slice(0, 3).map(ut => (
                              <span key={ut} style={{ padding: "1px 6px", background: "#ede9fe", color: "#5b21b6", borderRadius: "4px", fontSize: "11px", fontWeight: 500 }}>{ut}</span>
                            ))}
                            {utCount > 3 && <span style={{ fontSize: "11px", color: "#94a3b8" }}>+{utCount - 3}</span>}
                          </div>
                        )}
                      </td>

                      <td style={{ padding: "14px 16px", color: "#64748b", whiteSpace: "nowrap", fontSize: "12px" }}>
                        {new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>

                      <td style={{ padding: "14px 16px" }}>
                        <button onClick={() => setEditClient(c)}
                          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "6px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", fontWeight: 500, color: "#374151", cursor: "pointer" }}>
                          <HiPencil size={13} /> Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </>
  );
};

export default MasterHome;
